import { Controller } from "@hotwired/stimulus"

// Drives the topbar time-tracking pill. The visible clock is cosmetic and
// derives from started-at each tick (correct across sleep/throttling); the
// server heartbeat is what actually detects gaps. The immediate heartbeat on
// connect() is the "page load" contact that catches stale sessions.
//
// Usage:
//   <div data-controller="time-tracking-timer"
//        data-time-tracking-timer-started-at-value="2026-07-11T09:00:00Z"
//        data-time-tracking-timer-heartbeat-url-value="/admin/time_tracking/sessions/ID/heartbeat">
export default class extends Controller {
  static targets = ["elapsed"]
  static values = {
    startedAt: String,
    heartbeatUrl: String,
    heartbeatInterval: { type: Number, default: 60000 },
    // Pass heartbeat-value="false" for tick-only clocks (e.g. task-table row
    // cells); the topbar chip stays the single heartbeat source.
    heartbeat: { type: Boolean, default: true }
  }

  connect() {
    this.tickTimer = setInterval(() => this.tick(), 1000)
    this.tick()
    if (this.heartbeatValue) {
      this.heartbeatTimer = setInterval(() => this.heartbeat(), this.heartbeatIntervalValue)
      this.heartbeat()
    }
  }

  disconnect() {
    clearInterval(this.tickTimer)
    clearInterval(this.heartbeatTimer)
  }

  tick() {
    const seconds = Math.max(0, Math.floor((Date.now() - Date.parse(this.startedAtValue)) / 1000))
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, "0")
    const s = (seconds % 60).toString().padStart(2, "0")
    const text = h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`
    // Plural: the chip's clock plus the lazy-loaded panel's clock, which
    // Stimulus picks up automatically when the frame renders.
    this.elapsedTargets.forEach((el) => { el.textContent = text })
  }

  async heartbeat() {
    if (!this.heartbeatUrlValue) return // tick-only cells have no URL

    let data
    try {
      const response = await fetch(this.heartbeatUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        }
      })
      if (!response.ok) return
      data = await response.json()
    } catch {
      return // transient network failure; next interval retries
    }

    if (data.state === "running") return

    // Stopped elsewhere or force-stopped: fetch the resolution modal (which
    // also refreshes the pill) and render it.
    clearInterval(this.heartbeatTimer)
    const stream = await fetch(data.resolution_url, {
      headers: { "Accept": "text/vnd.turbo-stream.html" }
    })
    if (stream.ok && window.Turbo) {
      window.Turbo.renderStreamMessage(await stream.text())
    }
  }
}
